import type { Locale } from "@/lib/i18n/config";
import type {
  SellerProductReviewStatus,
  SellerProductReviewView,
} from "@/lib/api/types";

/**
 * Presentation helpers for the reviews screens (Trioty redesign). Kept together
 * so the grid card, table row, and summary card all derive avatars, status
 * tones, and aggregates the same way.
 */

/** Initials from a buyer name — first letters of the first two words. */
export function reviewInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean).slice(0, 2);
  if (parts.length === 0) return "؟";
  return parts
    .map((word) => word[0] ?? "")
    .join("")
    .toUpperCase();
}

/**
 * Saturated avatar hues cycled by a stable index (design AVPAL). The soft
 * background is mixed against the card surface so it reads in both themes.
 */
const AVATAR_HUES = [
  "#2563EB",
  "#7C3AED",
  "#15803D",
  "#B45309",
  "#DC2626",
  "#0369A1",
] as const;

export function reviewAvatar(index: number): { bg: string; fg: string } {
  const fg = AVATAR_HUES[((index % AVATAR_HUES.length) + AVATAR_HUES.length) % AVATAR_HUES.length];
  return {
    fg,
    bg: `color-mix(in srgb, ${fg} 14%, var(--card))`,
  };
}

/**
 * A stable non-negative index for a review id so avatar colours don't reshuffle
 * between renders/pages.
 */
export function reviewColorIndex(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

export type ReviewStatusTone = {
  label: string;
  bg: string;
  fg: string;
  dot: string;
};

/**
 * Status badge tone. The backend only models PUBLISHED / HIDDEN; both use
 * theme-aware tokens so the badge flips with dark mode.
 */
export function reviewStatusTone(
  status: SellerProductReviewStatus,
  labels: { published: string; hidden: string },
): ReviewStatusTone {
  if (status === "HIDDEN") {
    return {
      label: labels.hidden,
      bg: "var(--chip)",
      fg: "var(--text-muted)",
      dot: "var(--text-disabled)",
    };
  }
  return {
    label: labels.published,
    bg: "var(--success-100)",
    // --success-text, not --success-500: the 500 ink sits at 4.42:1 on the
    // soft success background in light mode. The -text token is the darkened
    // variant meant for text on that wash.
    fg: "var(--success-text)",
    dot: "var(--success-500)",
  };
}

/** Locale-aware absolute date (e.g. "27 Apr 2026"). */
export function formatReviewDate(value: string, locale: Locale): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale === "ar" ? "ar-EG" : "en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}

export type ReviewAggregates = {
  /** Mean rating over the provided items, or null when there are none. */
  average: number | null;
  /** Rating → count for 5..1 (index 0 === 5 stars). */
  distribution: { stars: number; count: number; pct: number }[];
  /** How many items were included in the aggregate. */
  sampleSize: number;
};

/**
 * Aggregate rating stats from whatever reviews are loaded. NOTE: this is a
 * best-effort over the current page only — there is no server aggregate
 * endpoint, which is why the summary card is flagged "Not supported".
 */
export function computeReviewAggregates(
  items: SellerProductReviewView[],
): ReviewAggregates {
  const counts = [0, 0, 0, 0, 0]; // index 0 === 1 star
  let sum = 0;
  for (const item of items) {
    const rating = Math.min(5, Math.max(1, Math.round(item.rating)));
    counts[rating - 1] += 1;
    sum += item.rating;
  }
  const sampleSize = items.length;
  const distribution = [5, 4, 3, 2, 1].map((stars) => {
    const count = counts[stars - 1];
    return {
      stars,
      count,
      pct: sampleSize > 0 ? Math.round((count / sampleSize) * 100) : 0,
    };
  });
  return {
    average: sampleSize > 0 ? sum / sampleSize : null,
    distribution,
    sampleSize,
  };
}
